import {
  PAGE_SIZES,
  type SavedDesign,
  type HeaderComponent,
  type TextBlock,
  type WatermarkBlock,
  type ContainerBlock,
  type ImageBlock,
  type ResumeBlock,
} from '../schemas/design.js'
import {
  resolveDesignTokens,
  escapeHtml,
  type TokenResolutionData,
} from './design-tokens.js'

/**
 * Renders a SavedDesign page to a self-contained absolutely-positioned HTML
 * fragment — the same paradigm the sandbox editor already uses (plain CSS,
 * no canvas/SVG), so this feeds directly into the existing Puppeteer →
 * pdf-lib pipeline. Ported from the sandbox's four style-builder functions
 * (resumeDisplayStyle/textDisplayStyle/containerDisplayStyle/
 * imageDisplayStyle), with editor-only selection/hover styling removed
 * since there is no interactive editing happening at render time.
 *
 * Callers are responsible for sanitizing TextBlock/WatermarkBlock `content`
 * before it reaches this function (see sanitize-design-html.ts) — this
 * renderer only escapes the *style attribute* value (protecting against a
 * free-text field like fontFamily breaking out of `style="..."`) and the
 * token-substituted values (see resolveDesignTokens), not the trusted HTML
 * content itself.
 */

export type PageVariant = 'first' | 'subsequent'

export interface PlacementRect {
  x: number
  y: number
  width: number
  height: number
  pageWidth: number
  pageHeight: number
}

export interface RenderDesignOptions {
  tokenData: TokenResolutionData
  /** Resolves a persisted ImageBlock.assetKey to a fetchable URL (S3 signed URL, local blob URL, etc). */
  resolveImageUrl?: (assetKey: string) => string | undefined
}

export interface RenderDesignResult {
  html: string
  placementRect: PlacementRect
  /**
   * Where write-up (cover page) text may safely go: the resume placeholder
   * rect trimmed clear of overlapping chrome (see writeupPlacementRect).
   */
  writeupRect: PlacementRect
}

// These build plain CSS strings (no HTML escaping) so they can be reused
// as-is by the client editor canvas via attr.style() — which sets the DOM
// style attribute programmatically (no HTML parsing/entity-decoding
// involved) — as well as by renderComponent below, which is building a raw
// HTML string and must escape when interpolating into a `style="..."`
// attribute. Escaping these strings unconditionally would corrupt
// legitimate quoted values (e.g. a `font-family: 'Times New Roman', serif`
// setting) when consumed via attr.style(), so escaping happens only at the
// HTML-interpolation site, not inside these builders.

function joinStyle(parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(' ')
}

function rotationStyle(rotation: number): string {
  return rotation
    ? `transform: rotate(${rotation}deg); transform-origin: center;`
    : ''
}

export function resumeSlotStyle(c: ResumeBlock): string {
  return joinStyle([
    'position: absolute;',
    `left: ${c.x}px; top: ${c.y}px;`,
    `width: ${c.width}px; height: ${c.height}px;`,
    'box-sizing: border-box; overflow: hidden;',
    rotationStyle(c.rotation),
  ])
}

export function textStyle(c: TextBlock | WatermarkBlock): string {
  return joinStyle([
    'position: absolute;',
    `left: ${c.x}px; top: ${c.y}px;`,
    `width: ${c.width}px; min-height: ${c.height}px;`,
    `font-size: ${c.fontSize}px;`,
    `font-family: ${c.fontFamily};`,
    `text-align: ${c.textAlign};`,
    `color: ${c.color};`,
    // A resolved token can be one unbreakable "word" wider than the block
    // (e.g. a long email); without this it overflows PAST the right edge —
    // even when right-aligned — instead of wrapping.
    'overflow-wrap: break-word;',
    'padding: 2px 4px; box-sizing: border-box;',
    `line-height: ${c.lineHeight};`,
    rotationStyle(c.rotation),
  ])
}

export function containerStyle(c: ContainerBlock): string {
  const borders = [
    `border-top: ${c.borderTop.width}px ${c.borderTop.style} ${c.borderTop.color};`,
    `border-right: ${c.borderRight.width}px ${c.borderRight.style} ${c.borderRight.color};`,
    `border-bottom: ${c.borderBottom.width}px ${c.borderBottom.style} ${c.borderBottom.color};`,
    `border-left: ${c.borderLeft.width}px ${c.borderLeft.style} ${c.borderLeft.color};`,
  ].join(' ')
  return joinStyle([
    'position: absolute;',
    `left: ${c.x}px; top: ${c.y}px;`,
    `width: ${c.width}px; height: ${c.height}px;`,
    `background-color: ${c.backgroundColor};`,
    `border-radius: ${c.radiusTL}px ${c.radiusTR}px ${c.radiusBR}px ${c.radiusBL}px;`,
    `padding: ${c.padding}px; box-sizing: border-box;`,
    borders,
    rotationStyle(c.rotation),
  ])
}

export function imageOuterStyle(c: ImageBlock): string {
  // overflow:hidden makes the rounding act as a mask over the image.
  const radii = [c.radiusTL, c.radiusTR, c.radiusBR, c.radiusBL]
  const hasRadius = radii.some(r => (r ?? 0) > 0)
  return joinStyle([
    'position: absolute;',
    `left: ${c.x}px; top: ${c.y}px;`,
    `width: ${c.width}px; height: ${c.height}px;`,
    'box-sizing: border-box; overflow: hidden;',
    hasRadius
      ? `border-radius: ${radii.map(r => `${r ?? 0}px`).join(' ')};`
      : '',
    rotationStyle(c.rotation),
  ])
}

export function imageInnerStyle(
  url: string | undefined,
  fit: 'fill' | 'contain' = 'fill'
): string {
  return joinStyle([
    'width: 100%; height: 100%;',
    url ? `background-image: url('${url.replace(/'/g, '%27')}');` : '',
    fit === 'contain'
      ? 'background-size: contain; background-repeat: no-repeat; background-position: center;'
      : 'background-size: 100% 100%;',
  ])
}

function renderComponent(
  component: HeaderComponent,
  tokenData: TokenResolutionData,
  resolveImageUrl?: (assetKey: string) => string | undefined
): string {
  switch (component.type) {
    case 'resume':
      // `data-main-container` is a hard requirement of packages/html-renderer
      // (it throws if the selector is missing, even though we override its
      // computed meta with our own known-exact placementRect) and is also
      // what the client's live-preview code already queries for — reusing
      // the existing convention rather than inventing a second one.
      return `<div data-main-container="true" style="${escapeHtml(resumeSlotStyle(component))}"></div>`
    case 'text':
    case 'watermark': {
      const content = resolveDesignTokens(component.content, tokenData)
      return `<div style="${escapeHtml(textStyle(component))}">${content}</div>`
    }
    case 'container':
      return `<div style="${escapeHtml(containerStyle(component))}"></div>`
    case 'image': {
      const url = component.assetKey
        ? resolveImageUrl?.(component.assetKey)
        : undefined
      return `<div style="${escapeHtml(imageOuterStyle(component))}"><div style="${escapeHtml(imageInnerStyle(url, component.fit))}"></div></div>`
    }
  }
}

function pagePlacementRect(
  components: HeaderComponent[],
  pageWidth: number,
  pageHeight: number
): PlacementRect {
  const resumeBlock = components.find(
    (c): c is ResumeBlock => c.type === 'resume'
  )
  if (!resumeBlock) {
    return {
      x: 0,
      y: 0,
      width: pageWidth,
      height: pageHeight,
      pageWidth,
      pageHeight,
    }
  }
  return {
    x: resumeBlock.x,
    y: resumeBlock.y,
    width: resumeBlock.width,
    height: resumeBlock.height,
    pageWidth,
    pageHeight,
  }
}

/**
 * The rect the write-up (cover page) text renders into: the resume
 * placeholder trimmed clear of any header/footer chrome that overlaps it.
 *
 * Designs routinely keep the placeholder *under* an opaque header band
 * (components may overlap; the placeholder always sits at the bottom
 * layer). That's invisible for resume pages — the resume is drawn beneath
 * the template overlay, so the band covers it — but write-up text is drawn
 * on top of the template, so it must start below anything that covers the
 * placeholder's top (and stop above anything covering its bottom).
 *
 * Trimming is vertical only, against text/container/image components that
 * horizontally intersect the placeholder: a component whose vertical center
 * sits in the placeholder's upper half raises the top edge below it, one in
 * the lower half lowers the bottom edge above it. Watermarks are skipped —
 * they are deliberate overlays. If trimming collapses the rect (chrome
 * covers the whole placeholder), the untrimmed placeholder is returned so
 * the write-up still renders somewhere sensible.
 */
export function writeupPlacementRect(
  components: HeaderComponent[],
  pageWidth: number,
  pageHeight: number
): PlacementRect {
  const placement = pagePlacementRect(components, pageWidth, pageHeight)
  let top = placement.y
  let bottom = placement.y + placement.height
  const placementCenterY = placement.y + placement.height / 2

  for (const c of components) {
    if (c.type === 'resume' || c.type === 'watermark') continue
    const horizontallyIntersects =
      c.x < placement.x + placement.width && c.x + c.width > placement.x
    if (!horizontallyIntersects) continue
    const cBottom = c.y + c.height
    if (cBottom <= top || c.y >= bottom) continue
    if (c.y + c.height / 2 <= placementCenterY) {
      top = Math.max(top, cBottom)
    } else {
      bottom = Math.min(bottom, c.y)
    }
  }

  if (bottom - top <= 0) return placement
  return { ...placement, y: top, height: bottom - top }
}

export function renderDesignToHtml(
  design: SavedDesign,
  pageVariant: PageVariant,
  options: RenderDesignOptions
): RenderDesignResult {
  const { w: pageWidth, h: pageHeight } = PAGE_SIZES[design.pageSize]
  const page =
    pageVariant === 'first' ? design.firstPage : design.subsequentPages
  const componentsHtml = page.components
    .map(c => renderComponent(c, options.tokenData, options.resolveImageUrl))
    .join('')

  // No page-wide background fill here, deliberately: this HTML is rendered
  // to a PDF page with printBackground:true and then used as a pdf-lib
  // overlay drawn *on top of* the actual resume page underneath. An opaque
  // fill (even white) would paint over the whole resume instead of just the
  // header/footer/watermark chrome. CSS's initial background-color is
  // transparent, so leaving it unset here is what makes everything outside
  // the explicitly-styled components (container backgrounds, text) a true
  // hole the resume page shows through.
  const html = `<div style="position: relative; width: ${pageWidth}px; height: ${pageHeight}px; overflow: hidden;">${componentsHtml}</div>`

  return {
    html,
    placementRect: pagePlacementRect(page.components, pageWidth, pageHeight),
    writeupRect: writeupPlacementRect(page.components, pageWidth, pageHeight),
  }
}
